import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ToastContext, type ToastOptions } from './toastContext';

/**
 * Transient notifications, top-center below the header (blueprint §3 —
 * docs/redesign/DESIGN_BLUEPRINT.md — the bottom of the viewport is a
 * reserved action zone that toasts must never occlude). One message at a
 * time, announced via a polite live region so screen readers catch async
 * states without a focus move.
 *
 * Create may dock the visual toast under the step map (`setVisualHost`) so
 * it cannot cover the map. The live region stays here either way.
 *
 * The provider must wrap the screen from OUTSIDE (RedesignProviders at the
 * route gate) — a screen's own Shell is its child, never an ancestor, so a
 * screen-level useToast() throws without the gate-level provider.
 *
 * Additive action variant (library port, bd Vidi-m8e): announce(message,
 * { actionLabel, onAction, durationMs }) renders the toast as an
 * interactive surface with a real button (no longer aria-hidden — hiding a
 * focusable control from the a11y tree is a violation). The screen-reader
 * channel stays the live region either way. Omitting the options keeps the
 * original pure-announcement behaviour byte-identical.
 */

const TOAST_DURATION_MS = 4000;

interface ToastAction {
  label: string;
  onAction: () => void;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [announcement, setAnnouncement] = useState('');
  const [visualMessage, setVisualMessage] = useState<string | null>(null);
  const [visualAction, setVisualAction] = useState<ToastAction | null>(null);
  const [visualHost, setVisualHost] = useState<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationFrame = useRef<number | null>(null);

  const dismiss = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    setAnnouncement('');
    setVisualMessage(null);
    setVisualAction(null);
  }, []);

  const announce = useCallback(
    (next: string, options?: ToastOptions) => {
      dismiss();

      // Keep the empty live region mounted so assistive tech subscribes before
      // copy arrives. Clear it for one frame so repeating the same message is
      // announced again; the visual toast is a separate surface.
      animationFrame.current = requestAnimationFrame(() => {
        setAnnouncement(next);
        setVisualMessage(next);
        setVisualAction(
          options?.actionLabel && options?.onAction
            ? { label: options.actionLabel, onAction: options.onAction }
            : null,
        );
        timer.current = setTimeout(dismiss, options?.durationMs ?? TOAST_DURATION_MS);
      });
    },
    [dismiss],
  );

  useEffect(() => dismiss, [dismiss]);

  const value = useMemo(() => ({ announce, setVisualHost }), [announce]);

  const docked = Boolean(visualHost);
  const toastClass = `rd rd-toast${visualAction ? ' rd-toast-interactive' : ''}${docked ? ' rd-toast-docked' : ''}${visualMessage ? ' is-visible' : ''}`;

  const toastSurface = visualAction ? (
    <div className={toastClass}>
      <span>{visualMessage ?? ''}</span>
      <button
        type="button"
        className="rd-toast-action"
        onClick={() => {
          visualAction.onAction();
          dismiss();
        }}
      >
        {visualAction.label}
      </button>
    </div>
  ) : (
    <div className={toastClass} aria-hidden="true" hidden={!visualMessage}>
      {visualMessage ?? ''}
    </div>
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="rd-live-region" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      {visualHost ? createPortal(toastSurface, visualHost) : toastSurface}
    </ToastContext.Provider>
  );
}
